import React, { useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, MenuItem, Slider, TextField, Typography,
} from '@mui/material';
import { fetchAccountSettings, probeUploadLimit, updateAccountSettings } from './api.js';
import SecuritySettings from './SecuritySettings';
import ShareLinksSettings from './ShareLinksSettings';

const MiB = 1024 * 1024;
const toMB = (bytes) => Math.round((Number(bytes) / MiB) * 10) / 10;
const formatWhen = (ms) => new Date(ms).toLocaleString('id-ID', { dateStyle: 'medium', timeStyle: 'short' });

// Setelan akun. Disimpan di bot (ikut data akun di Discord), jadi sama di
// semua perangkat dan juga dipakai upload lewat WebDAV.
export default function SettingsDialog({ open, fileManager, onClose, onSaved }) {
    const [settings, setSettings] = useState(null);
    const [uploadCount, setUploadCount] = useState(1);
    const [addTotal, setAddTotal] = useState('');
    const [trashDays, setTrashDays] = useState(0);
    const [chunkMB, setChunkMB] = useState('');
    const [probing, setProbing] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saved, setSaved] = useState(null);

    const apply = (data) => {
        setSettings(data);
        setUploadCount(data.uploadWebhooks);
        setAddTotal(data.webhookCount < data.maxWebhooks ? data.webhookCount + 1 : '');
        setTrashDays(Number(data.trashRetentionDays) || 0);
        setChunkMB(String(toMB(data.uploadChunkBytes)));
        const storage = fileManager && fileManager.discordFileStorage;
        if (storage) {
            storage.setWebhookUrls(data.webhookUrls || []);
            storage.setUploadWebhookCount(data.uploadWebhooksAll ? null : data.uploadWebhooks);
            storage.setChunkSize(data.uploadChunkBytes);
        }
        if (onSaved) onSaved(data);
    };

    useEffect(() => {
        if (!open || !fileManager) return;
        setError(null);
        setSaved(null);
        setLoading(true);
        fetchAccountSettings(fileManager.userId)
            .then(apply)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, fileManager]);

    // `message`: teks sukses, atau fungsi (setelan baru) -> teks.
    const runAction = async (request, message) => {
        setSaving(true);
        setError(null);
        setSaved(null);
        try {
            const data = await request();
            apply(data);
            setSaved(typeof message === 'function' ? message(data) : message);
        } catch (e) {
            setError(e.message);
        } finally {
            setSaving(false);
        }
    };
    const save = (changes, message) => runAction(() => updateAccountSettings(fileManager.userId, changes), message);
    const probe = async () => {
        setProbing(true);
        await runAction(() => probeUploadLimit(fileManager.userId),
            (data) => `Discord menerima potongan ${toMB(data.uploadChunkBytes)} MB. Upload baru memakai ukuran ini.`);
        setProbing(false);
    };

    const total = settings ? settings.webhookCount : 0;
    const changed = settings && uploadCount !== settings.uploadWebhooks;
    const chunkValue = Number(chunkMB);
    const chunkValid = settings && chunkMB !== '' && chunkValue >= toMB(settings.uploadChunkMin) && chunkValue <= toMB(settings.uploadChunkMax);
    const chunkChanged = settings && chunkValid && (toMB(settings.uploadChunkBytes) !== Math.round(chunkValue * 10) / 10 || Boolean(settings.uploadChunkLoweredAt));
    const addOptions = [];
    if (settings) for (let n = total + 1; n <= settings.maxWebhooks; n++) addOptions.push(n);

    return (
        <Dialog open={open} onClose={() => { if (!saving) onClose(); }} maxWidth="sm" fullWidth>
            <DialogTitle>Setelan</DialogTitle>
            <DialogContent>
                {loading && !settings && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={28} /></Box>}
                {settings && (
                    <>
                        <Typography variant="subtitle2" sx={{ mt: 1 }}>Webhook untuk upload</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            Upload baru memakai <b>{uploadCount}</b> dari {total} webhook{uploadCount === total ? ' (semua)' : ''}.
                        </Typography>
                        {total > 1 ? (
                            <Box sx={{ px: 1.5, mt: 1 }}>
                                <Slider
                                    value={uploadCount}
                                    min={1}
                                    max={total}
                                    step={1}
                                    marks={[{ value: 1, label: '1' }, { value: total, label: String(total) }]}
                                    valueLabelDisplay="auto"
                                    onChange={(e, value) => setUploadCount(value)}
                                    disabled={saving}
                                />
                            </Box>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Akun ini baru punya 1 webhook.</Typography>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Tiap webhook mengirim satu potongan file sekaligus, jadi makin banyak webhook, upload makin cepat tapi makin berat
                            untuk koneksi. Kalau upload sering gagal atau putus (misal pakai data HP), kecilkan ke 2-3.
                        </Typography>
                        <Alert severity="info" sx={{ mt: 1.5 }}>
                            Hanya berlaku untuk upload baru (web dan WebDAV). File yang sudah ada tidak terpengaruh: tiap file tetap dibuka
                            lewat webhook yang dulu mengunggahnya.
                        </Alert>
                        <Button variant="contained" sx={{ mt: 1.5 }} disabled={!changed || saving}
                            onClick={() => save({ uploadWebhooks: uploadCount >= total ? 0 : uploadCount }, `Upload baru sekarang memakai ${uploadCount} webhook.`)}>
                            Simpan
                        </Button>

                        <Divider sx={{ my: 2.5 }} />
                        <Typography variant="subtitle2">Tambah webhook</Typography>
                        {settings.canAddWebhooks && total < settings.maxWebhooks ? (
                            <>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                    Bot membuatkan webhook baru di channel penyimpanan akunmu. Webhook lama tidak diubah. Batas Discord{' '}
                                    {settings.maxWebhooks} webhook per channel.
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 1.5, flexWrap: 'wrap' }}>
                                    <TextField select size="small" label="Jumlah total" value={addTotal} onChange={(e) => setAddTotal(Number(e.target.value))} sx={{ minWidth: 150 }} disabled={saving}>
                                        {addOptions.map((n) => <MenuItem key={n} value={n}>{n} webhook (+{n - total})</MenuItem>)}
                                    </TextField>
                                    <Button variant="outlined" disabled={saving || !addTotal}
                                        onClick={() => save({ webhookTotal: addTotal }, `Webhook ditambah, sekarang ${addTotal} webhook.`)}>
                                        {saving ? 'Memproses...' : 'Tambah'}
                                    </Button>
                                </Box>
                            </>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {settings.canAddWebhooks
                                    ? `Sudah ${total} webhook, batas maksimal Discord per channel.`
                                    : 'Channel penyimpanan akun ini tidak dikenal bot, jadi webhook tidak bisa ditambah dari sini.'}
                            </Typography>
                        )}

                        <Divider sx={{ my: 2.5 }} />
                        <Typography variant="subtitle2">Ukuran potongan upload</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            File dipecah per <b>{toMB(settings.uploadChunkBytes)} MB</b> sebelum dikirim ke Discord
                            {settings.uploadChunkBytes === settings.uploadChunkDefault ? ' (bawaan)' : ''}.
                        </Typography>
                        {settings.uploadChunkLoweredAt && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                Diturunkan otomatis pada {formatWhen(settings.uploadChunkLoweredAt)} karena Discord menolak potongan yang lebih besar.
                            </Alert>
                        )}
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Kalau Discord menurunkan batas upload, ukuran ini turun sendiri dan upload yang sedang jalan diulang dengan potongan
                            lebih kecil (tidak gagal). Kalau Discord menaikkan batasnya, tekan "Cek batas Discord" (bot mengirim file uji lalu
                            langsung menghapusnya) atau isi sendiri. Berlaku untuk web dan WebDAV, file lama tidak terpengaruh.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 1.5, flexWrap: 'wrap' }}>
                            <TextField size="small" type="number" label="Ukuran (MB)" value={chunkMB} onChange={(e) => setChunkMB(e.target.value)}
                                inputProps={{ min: toMB(settings.uploadChunkMin), max: toMB(settings.uploadChunkMax), step: 0.5 }}
                                error={chunkMB !== '' && !chunkValid} sx={{ width: 140 }} disabled={saving} />
                            <Button variant="outlined" disabled={saving || !chunkChanged}
                                onClick={() => save({ uploadChunkMB: chunkValue }, `Upload baru sekarang dipecah per ${toMB(chunkValue * MiB)} MB.`)}>
                                Simpan
                            </Button>
                            <Button variant="text" disabled={saving} onClick={probe}>
                                {probing ? 'Mengecek...' : 'Cek batas Discord'}
                            </Button>
                        </Box>
                        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                            {toMB(settings.uploadChunkMin)} sampai {toMB(settings.uploadChunkMax)} MB. Potongan lebih besar berarti lebih sedikit upload paralel.
                            {settings.uploadChunkCheckedAt ? ` Terakhir dicek ${formatWhen(settings.uploadChunkCheckedAt)}.` : ''}
                        </Typography>

                        <Divider sx={{ my: 2.5 }} />
                        <Typography variant="subtitle2">Hapus otomatis Trash</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Khusus akun ini. Item yang sudah lebih lama dari batas ini di Trash dihapus permanen oleh bot (dicek tiap 6 jam)
                            dan tidak bisa dikembalikan lagi.
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 1.5, flexWrap: 'wrap' }}>
                            <TextField select size="small" label="Hapus otomatis" value={trashDays} onChange={(e) => setTrashDays(Number(e.target.value))} sx={{ minWidth: 240 }} disabled={saving}>
                                <MenuItem value={0}>Manual (tidak dihapus otomatis)</MenuItem>
                                <MenuItem value={15}>Setelah 15 hari</MenuItem>
                                <MenuItem value={30}>Setelah 30 hari</MenuItem>
                            </TextField>
                            <Button variant="outlined" disabled={saving || trashDays === (Number(settings.trashRetentionDays) || 0)}
                                onClick={() => {
                                    if (trashDays > 0 && !window.confirm(`Item yang sudah lebih dari ${trashDays} hari di Trash akan langsung dihapus PERMANEN. Lanjutkan?`)) return;
                                    save({ trashRetentionDays: trashDays }, trashDays > 0
                                        ? `Trash sekarang dihapus otomatis setelah ${trashDays} hari.`
                                        : 'Trash tidak lagi dihapus otomatis. Kosongkan manual lewat tombol "Kosongkan Trash".');
                                }}>
                                Simpan
                            </Button>
                        </Box>
                    </>
                )}
                {saved && <Alert severity="success" sx={{ mt: 2 }}>{saved}</Alert>}
                {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

                <Divider sx={{ my: 2.5 }} />
                <SecuritySettings open={open} />

                <Divider sx={{ my: 2.5 }} />
                <ShareLinksSettings open={open} fileManager={fileManager} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Tutup</Button>
            </DialogActions>
        </Dialog>
    );
}
